import {
  doc,
  deleteDoc,
  collection,
  getDocs,
  query,
  where,
  getDoc
} from 'firebase/firestore';
import { deleteUser } from 'firebase/auth';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

/**
 * Delete all user data from Firestore, Storage, and Firebase Auth
 * @param {Object} user - Firebase Auth user object
 * @param {string} confirmation - Must be "DELETE_MY_ACCOUNT" for security
 */
export const deleteUserData = async (user, confirmation) => {
  try {
    console.log(`Starting account deletion for UID: ${user.uid}`);

    // SECURITY: Require explicit confirmation
    if (confirmation !== 'DELETE_MY_ACCOUNT') {
      throw new Error('Account deletion requires confirmation with "DELETE_MY_ACCOUNT"');
    }

    const uid = user.uid;

    // 1. Delete Firestore user data
    console.log('Deleting Firestore user data...');

    // Delete main user document
    const userDocRef = doc(db, `users/${uid}`);
    await deleteDoc(userDocRef);

    // Delete all subcollections
    const subcollections = ['vehicles', 'events', 'sessions', 'circuits'];
    for (const subcol of subcollections) {
      const subColRef = collection(db, `users/${uid}/${subcol}`);
      const snapshot = await getDocs(subColRef);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
      console.log(`Deleted ${snapshot.docs.length} documents from ${subcol}`);
    }

    // 2. Delete Stripe customer data from Firestore
    console.log('Deleting Stripe customer data...');
    try {
      // First try to delete by UID (primary method)
      try {
        const customerDocRef = doc(db, `customers/${uid}`);
        const customerDoc = await getDoc(customerDocRef);

        if (customerDoc.exists()) {
          await deleteDoc(customerDocRef);
          console.log('Deleted customer document by UID');
        } else {
          console.log('Customer document not found by UID, may have been created with different key');
        }
      } catch (error) {
        console.warn('Error deleting customer document by UID:', error.message);
      }

      // Delete checkout sessions subcollection
      try {
        const checkoutSessionsRef = collection(db, `customers/${uid}/checkout_sessions`);
        const checkoutSnapshot = await getDocs(checkoutSessionsRef);
        const checkoutDeletePromises = checkoutSnapshot.docs.map(doc => deleteDoc(doc.ref));
        await Promise.all(checkoutDeletePromises);
        console.log(`Deleted ${checkoutSnapshot.docs.length} checkout sessions`);
      } catch (error) {
        console.warn('Error deleting checkout sessions:', error.message);
      }



      // Also try to find and delete customer documents by email if user has one
      try {
        const userDoc = await getDoc(doc(db, `users/${uid}`));
        if (userDoc.exists() && userDoc.data().email) {
          const customersRef = collection(db, 'customers');
          const emailQuery = query(customersRef, where('email', '==', userDoc.data().email));
          const emailSnapshot = await getDocs(emailQuery);

          for (const customerDoc of emailSnapshot.docs) {
            if (customerDoc.id !== uid) { // Don't double-delete
              try {
                await deleteDoc(customerDoc.ref);
                console.log(`Deleted customer document by email: ${customerDoc.id}`);
              } catch (error) {
                console.warn(`Error deleting customer document ${customerDoc.id}:`, error.message);
              }
            }
          }
        }
      } catch (error) {
        console.warn('Error in email-based customer deletion:', error.message);
      }

    } catch (firestoreError) {
      console.warn('Error deleting Firestore Stripe data (may be due to security rules):', firestoreError.message);
      // This is not critical - the data may still be deleted or may be restricted by security rules
    }

    // 3. Delete Firebase Storage files
    console.log('Deleting Firebase Storage files...');
    try {
      const userStorageRef = ref(storage, `backups/${uid}`);
      const result = await listAll(userStorageRef);

      if (result.items.length > 0) {
        const deletePromises = result.items.map(item => deleteObject(item));
        await Promise.all(deletePromises);
        console.log(`Deleted ${result.items.length} backup files`);
      }

      // Also check for any other user files (images, etc.)
      const imagesRef = ref(storage, `images/${uid}`);
      const imagesResult = await listAll(imagesRef);
      if (imagesResult.items.length > 0) {
        const imageDeletePromises = imagesResult.items.map(item => deleteObject(item));
        await Promise.all(imageDeletePromises);
        console.log(`Deleted ${imagesResult.items.length} image files`);
      }
    } catch (storageError) {
      console.warn('Error deleting storage files:', storageError);
    }

    // 4. Delete Firebase Auth user
    console.log('Deleting Firebase Auth user...');
    await deleteUser(user);

    // 5. Note: Stripe customer deletion should be handled separately
    // via Stripe API or Firebase extension webhooks
    console.log(`Successfully deleted all data for user ${uid}`);
    return { success: true, message: 'User data deleted successfully' };

  } catch (error) {
    console.error('Error during user deletion:', error);
    throw new Error(`Failed to delete user data: ${error.message}`);
  }
};

/**
 * Admin function to delete user by UID (for support requests)
 * @param {string} uid - User ID to delete
 */
export const deleteUserByUid = async (uid) => {
  try {
    console.log(`Admin deletion requested for UID: ${uid}`);

    // Get user document to check if exists
    const userDocRef = doc(db, `users/${uid}`);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      throw new Error('User not found in database');
    }

    // Delete Firestore data (same as user deletion)
    const subcollections = ['vehicles', 'events', 'sessions', 'circuits'];
    for (const subcol of subcollections) {
      const subColRef = collection(db, `users/${uid}/${subcol}`);
      const snapshot = await getDocs(subColRef);
      const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    }

    await deleteDoc(userDocRef);

    // Delete Stripe data
    try {
      // First try to delete by UID (primary method)
      const customerDocRef = doc(db, `customers/${uid}`);
      const customerDoc = await getDoc(customerDocRef);

      if (customerDoc.exists()) {
        await deleteDoc(customerDocRef);
        console.log('Deleted customer document by UID');
      } else {
        console.log('Customer document not found by UID, may have been created with different key');
      }

      const checkoutSessionsRef = collection(db, `customers/${uid}/checkout_sessions`);
      const checkoutSnapshot = await getDocs(checkoutSessionsRef);
      const checkoutDeletePromises = checkoutSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(checkoutDeletePromises);
      console.log(`Deleted ${checkoutSnapshot.docs.length} checkout sessions`);

      // Also try to find and delete customer documents by email if user has one
      if (userDoc.exists() && userDoc.data().email) {
        const customersRef = collection(db, 'customers');
        const emailQuery = query(customersRef, where('email', '==', userDoc.data().email));
        const emailSnapshot = await getDocs(emailQuery);

        for (const customerDoc of emailSnapshot.docs) {
          if (customerDoc.id !== uid) { // Don't double-delete
            await deleteDoc(customerDoc.ref);
            console.log(`Deleted customer document by email: ${customerDoc.id}`);
          }
        }
      }

    } catch (stripeError) {
      console.warn('Error deleting Stripe data:', stripeError);
    }

    // Delete Storage files
    try {
      const userStorageRef = ref(storage, `backups/${uid}`);
      const result = await listAll(userStorageRef);
      if (result.items.length > 0) {
        const deletePromises = result.items.map(item => deleteObject(item));
        await Promise.all(deletePromises);
      }

      const imagesRef = ref(storage, `images/${uid}`);
      const imagesResult = await listAll(imagesRef);
      if (imagesResult.items.length > 0) {
        const imageDeletePromises = imagesResult.items.map(item => deleteObject(item));
        await Promise.all(imageDeletePromises);
      }
    } catch (storageError) {
      console.warn('Error deleting storage files:', storageError);
    }

    console.log(`Admin deletion completed for UID: ${uid}`);
    console.log('WARNING: Firebase Auth user must be deleted separately by admin');

    return {
      success: true,
      message: 'User data deleted from database. Firebase Auth user must be deleted separately.',
      warning: 'Firebase Auth user deletion requires admin access'
    };

  } catch (error) {
    console.error('Error during admin user deletion:', error);
    throw new Error(`Failed to delete user data: ${error.message}`);
  }
};